/**
 * Privacy module — CMP detection, TCF/USP reading, InMobi bootstrap, and the
 * CCPA opt-out surface.
 *
 * Left out of bundles built for visitors under no privacy regime. Consent then
 * stays `unknown` and `co` is omitted; see `core/consent/port.ts`.
 */

import type { SdkModuleDefinition } from '../types';
import { getSdkState } from '../../core/state';
import { setConsentGate, type PrivacyApi } from '../../core/consent/port';
import { PrivacyManager } from './manager';

export type PrivacyModuleApi = PrivacyApi;

export const privacyModule: SdkModuleDefinition<PrivacyModuleApi> = {
  id: 'privacy',
  isEnabled: () => true,
  install(ctx) {
    const state = getSdkState();
    const config = ctx.getConfig();
    const options = ctx.getOptions();

    const manager = new PrivacyManager({
      rootDomain: config.website.root_domain,
      cmp: config.cmp,
      loadCmp: options.loadCmp,
      consentOverride: options.consent,
      privacyInit: options.privacy,
      engine: state.engine,
    });

    state.privacy = manager.privacy;

    // Resolution runs from the gate rather than here: install happens before the
    // sdk_init event, which goes out while consent is still unresolved.
    setConsentGate({
      async settled() {
        await manager.start();
        if (manager.shouldDeferAnalyticsLifecycle()) {
          await manager.waitForFirstCmpEvent();
        }
      },
      destroy: () => manager.destroy(),
    });

    return manager.privacy;
  },
};
